import { markRaw, reactive } from 'vue';
import { closePanel, openPanel, replacePanelContent, stack } from '@/shared/panelStack.js';
import RedeemPinPanel from '@/shared/panels/RedeemPinPanel.vue';
import RedeemProviderPanel from '@/shared/panels/RedeemProviderPanel.vue';

// Drives a voucher-code journey: one POST, then whichever of the second steps that answer
// asks for. V1 does this across three page loads with the in-progress code, candidate list
// and chosen provider in five session keys; here the state lives in one of these objects for
// the few seconds it exists.
//
// A factory rather than a module singleton, because there are now two of these journeys on
// the site - the header's Redeem bar and the /check-balance page - and they post to different
// endpoints. They can appear on the same page, so a single shared `checkUrl` and `panelKey`
// would have them overwriting each other's.
//
// What both journeys share is everything up to the answer: the PIN step, the provider picker,
// and the two failures that are not about the voucher at all. Those live here. What they do
// with a resolved voucher is their own business, and arrives as `onOutcome`.
//
// The panels are genuinely dumb - they collect input and call the flow handed to them as a
// prop. Routing the outcome stays here, so no panel needs to import another (the PIN step can
// lead to the picker and the picker back to the PIN step, which as direct imports would be a
// cycle).

/**
 * @param {object} options
 * @param {string} options.url - the endpoint to POST the code to.
 * @param {function} [options.onOutcome] - handles the outcomes this journey owns. Called with
 *   (data, helpers); return true to say it was handled, anything else to fall through to the
 *   shared defaults below.
 * @param {string} [options.panelTitle] - heading for the PIN and provider step panels.
 */
export function createVoucherFlow({ url, onOutcome = null, panelTitle = 'Your Voucher' }) {
    const state = reactive({
        busy: false,
        error: null,
        // Whether a step panel is currently on screen. The bar shows errors only when it is
        // the visible surface; otherwise the open panel shows them instead.
        panelOpen: false,
    });

    let panelKey = null;

    /**
     * @param {{code: string, pin?: string, provider?: string}} payload
     */
    async function submit(payload) {
        if (state.busy) {
            return;
        }

        state.busy = true;
        state.error = null;

        try {
            const { data } = await axios.post(url, payload);
            routeOutcome(data);
        } catch (e) {
            // Being throttled is not a failure of the voucher or of the site, and it is the
            // one error here a customer can actually act on, so it says what to do. Both
            // voucher endpoints allow ten an hour - see docs/rate-limiting.md.
            if (e.response?.status === 429) {
                state.error = 'You have checked a few vouchers recently. '
                    + 'Please wait a little while and try again.';
            } else {
                // A validation failure (422) is the only other expected error shape; anything
                // else is ours and gets the same honest message rather than a guess at the cause.
                state.error = e.response?.data?.message
                    ?? 'Something went wrong checking that voucher - please try again.';
            }
        } finally {
            state.busy = false;
        }
    }

    function routeOutcome(data) {
        switch (data.outcome) {
            // The two steps every journey shares. Both endpoints answer them in the same
            // shape, which is what Resolution::stepPayload() exists to guarantee.
            case 'needs_pin':
                // The flow goes with it so the panel can submit the next step through this
                // same instance. Panels render in another app root, reached by key, so there
                // is no provide/inject path to hand it down.
                return show(RedeemPinPanel, { code: data.code, provider: data.provider, flow });

            case 'needs_provider':
                return show(RedeemProviderPanel, { code: data.code, providers: data.providers, flow });
        }

        if (onOutcome?.(data, { state, show, end }) === true) {
            return;
        }

        switch (data.outcome) {
            case 'provider_error':
                // Distinct from not_found on purpose: this is our problem, and telling someone
                // their valid voucher is unrecognised would send them to the wrong place.
                state.error = 'We could not check that voucher just now. Please try again shortly, '
                    + 'or contact us if it keeps happening.';

                return;

            case 'not_found':
            default:
                state.error = 'We do not recognise that voucher number. Please check it and try again.';
        }
    }

    // Going deeper in this journey never has anything to come back to - a PIN step, once
    // answered, is done - so an already-open panel has its content replaced rather than
    // gaining another on top. That is the rule panelStack.js documents for the two.
    function show(component, props = {}, title = panelTitle) {
        const open = panelKey !== null && stack.some((entry) => entry.key === panelKey && ! entry.closing);

        if (open) {
            replacePanelContent({ component, title, props }, panelKey);
        } else {
            panelKey = openPanel({ component, title, props });
        }

        state.panelOpen = true;
    }

    function end() {
        if (panelKey !== null) {
            closePanel(panelKey);
            panelKey = null;
        }

        state.panelOpen = false;
        state.error = null;
    }

    // markRaw because this gets passed as a panel prop, and panelStack keeps its entries in a
    // reactive array - without it the whole flow, functions included, would be deep-proxied.
    // `state` is reactive in its own right, so nothing is lost.
    const flow = markRaw({ state, submit, show, end });

    return flow;
}
